import type { SideMenuItem } from '@eleo-rh/core-ui'
import Link from 'components/UI/Link'
import dayjs from 'config/dayjs'
import { permissions } from 'constants/permissions'
import type { Settings } from 'shared/types'

interface GetItemsProps {
  tableauURL: string
  i18n: (key: string) => string
  perimeter?: {
    'n-1': boolean
    'n-X': boolean
  }
  settings: Settings
  profileType: string
}

export type LayoutSiderItem = Partial<SideMenuItem> & {
  permissions?: string[]
  feature?: string
  enabled?: boolean
  children?: LayoutSiderItem[]
}

export interface LayoutSiderItems {
  coreItems: LayoutSiderItem[]
  adminItems: LayoutSiderItem[]
}

function collectPermissions(items: LayoutSiderItem[]): string[] {
  return items.flatMap(item => [
    ...(item.permissions || []),
    ...(item.children ? collectPermissions(item.children) : []),
  ])
}

export function getItemPermissions(items: LayoutSiderItem[]): string[] {
  return [...new Set(collectPermissions(items))]
}

export const items = ({ tableauURL, i18n, perimeter, settings, profileType }: GetItemsProps): LayoutSiderItems => {
  const cascadeParam = perimeter?.['n-1'] && perimeter?.['n-X'] ? '&cascade=false' : ''

  const reporting: LayoutSiderItem[] =
    tableauURL && settings?.tableauEnabled
      ? [
          {
            label: <Link to={tableauURL}>{i18n('menu.reporting')}</Link>,
            icon: 'chart-pie',
            permissions: permissions.reporting,
          },
        ]
      : []

  const coreItems: LayoutSiderItem[] = [
    {
      key: 'dashboard',
      label: <Link to="dashboard">{i18n('menu.my_space')}</Link>,
      icon: 'user',
      enabled: profileType === 'collaborator',
    },
    {
      key: 'catalog',
      label: <Link to="catalog">{i18n('menu.catalog')} </Link>,
      icon: 'book-open',
      permissions: permissions.catalog,
    },
    {
      key: 'training-management',
      label: <Link to="training-management">{i18n('menu.trainings_management')}</Link>,
      icon: 'book-open-reader',
      permissions: permissions.trainingManagement,
    },
    {
      key: 'needs-summary',
      label: <Link to="needs-summary">{i18n('menu.collection_need')}</Link>,
      icon: 'clipboard-list',
      permissions: permissions.needsSummary,
    },
    {
      key: 'sessions',
      label: <Link to="sessions">{i18n('menu.collection_sessions')}</Link>,
      icon: 'calendar-check',
      permissions: permissions.sessionView,
    },
    {
      key: `planning?date=${dayjs(new Date()).format('YYYY-MM-DD')}`,
      label: (
        <Link to={`planning?date=${dayjs(new Date()).format('YYYY-MM-DD')}`}>
          {i18n('menu.collection_reservations')}
        </Link>
      ),
      icon: 'calendar-day',
      permissions: permissions.roomBookingCanView,
      enabled: settings?.roomBookingEnabled,
    },
    {
      key: 'training-plan',
      label: <Link to="training-plan">{i18n('menu.training_plan')}</Link>,
      icon: 'chart-diagram',
      permissions: permissions.trainingPlan,
    },
    {
      key: 'deleted-wishes',
      label: <Link to="deleted-wishes">{i18n('menu.deleted_wishes')}</Link>,
      icon: 'trash',
      permissions: permissions.deletedWishes,
    },
    {
      key: 'accreditations?user.enabled=true',
      label: <Link to="accreditations?user.enabled=true">{i18n('menu.accreditation_board.hse')}</Link>,
      icon: 'chart-network',
      permissions: permissions.accreditationBoard,
    },
    {
      key: 'renewable-trainings?user.enabled=true',
      label: <Link to="renewable-trainings?user.enabled=true">{i18n('menu.accreditation_board')}</Link>,
      icon: 'chart-network',
      permissions: permissions.renewableTrainingBoard,
    },
    {
      key: 'collectives',
      label: <Link to="collectives">{i18n('menu.collectives_management')}</Link>,
      icon: 'users',
      permissions: permissions.collectives,
    },
    {
      key: `my-perimeter?enabled=true${cascadeParam}`,
      label: <Link to={`my-perimeter?enabled=true${cascadeParam}`}>{i18n('menu.perimeter')}</Link>,
      icon: 'user-group',
      permissions: permissions.myPerimeter,
      feature: 'myPerimeter',
    },
    {
      key: `training-history?${cascadeParam}`,
      label: <Link to={`training-history?${cascadeParam}`}>{i18n('menu.training_history')}</Link>,
      icon: 'clock-rotate-left',
      permissions: permissions.trainingHistory,
    },
    ...reporting,
  ]

  const adminItems: LayoutSiderItem[] = [
    {
      key: 'menu.admin_settings',
      label: i18n('menu.admin_settings'),
      children: [
        {
          key: 'admin/profiles',
          label: <Link to="admin/profiles">{i18n('menu.admin_settings_profiles')}</Link>,
          permissions: permissions.adminProfiles,
        },
        {
          key: 'admin/sub-profiles',
          label: <Link to="admin/sub-profiles">{i18n('menu.admin_settings_subprofiles')}</Link>,
          permissions: permissions.adminSubProfiles,
        },
        {
          key: 'admin/settings',
          label: <Link to="admin/settings">{i18n('menu.admin_settings_global')}</Link>,
          permissions: permissions.adminSettings,
        },
        {
          key: 'admin/collaborators',
          label: <Link to="admin/collaborators">{i18n('admin.crud.collaborators.view')}</Link>,
          permissions: permissions.adminCollaborators,
        },
        {
          key: 'admin/doc-templates',
          label: <Link to="admin/doc-templates">{i18n('menu.admin_doc_templates')}</Link>,
          permissions: permissions.adminDocTemplates,
        },
        {
          key: 'admin/populations',
          label: <Link to="admin/populations">{i18n('menu.admin_populations')}</Link>,
          permissions: permissions.adminPopulations,
        },
        {
          key: 'admin/applications',
          label: <Link to="admin/applications">{i18n('menu.admin_applications')}</Link>,
          permissions: permissions.adminApplications,
        },
        {
          key: 'admin/perimeters/training',
          label: <Link to="admin/perimeters/training">{i18n('menu.admin_training_perimeter')}</Link>,
          permissions: permissions.adminPerimetersTraining,
        },
        {
          key: 'admin/perimeters/accreditation',
          label: <Link to="admin/perimeters/accreditation">{i18n('menu.admin_accreditation_perimeter')}</Link>,
          permissions: permissions.adminPerimetersAccreditation,
        },
      ],
    },
    {
      key: 'menu.admin_training',
      label: i18n('menu.admin_training'),
      permissions: permissions.adminTrainings,
      children: [
        {
          key: 'admin/training/categories',
          label: <Link to="admin/training/categories">{i18n('menu.admin_training_categories')}</Link>,
        },
        {
          key: 'admin/training/themes',
          label: <Link to="admin/training/themes">{i18n('menu.admin_training_themes')}</Link>,
        },
        {
          key: 'admin/training/plans',
          label: <Link to="admin/training/plans">{i18n('menu.admin_training_plans')}</Link>,
        },
        {
          key: 'admin/training/budgets',
          label: <Link to="admin/training/budgets">{i18n('admin.crud.budgets.list')}</Link>,
        },
        {
          key: 'admin/training/trainers',
          label: <Link to="admin/training/trainers">{i18n('admin_trainers')}</Link>,
        },
        {
          key: 'admin/teaching-methods',
          label: <Link to="admin/teaching-methods">{i18n('menu.admin_teaching_methods')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_wish',
      label: i18n('menu.admin_wish'),
      permissions: permissions.adminWishes,
      children: [
        {
          key: 'admin/training-wish/reasons',
          label: <Link to="admin/training-wish/reasons">{i18n('menu.admin_wish_reasons')}</Link>,
        },
        {
          key: 'admin/training-wish/perimeters',
          label: <Link to="admin/training-wish/perimeters">{i18n('menu.admin_wish_perimeters')}</Link>,
        },
        {
          key: 'admin/training-wish/orientations',
          label: <Link to="admin/training-wish/orientations">{i18n('menu.admin_wish_orientations')}</Link>,
        },
        {
          key: 'admin/training-wish/types',
          label: <Link to="admin/training-wish/types">{i18n('menu.admin_wish_types')}</Link>,
        },
        {
          key: 'admin/training-wish/refusal-reasons',
          label: <Link to="admin/training-wish/refusal-reasons">{i18n('menu.admin_wish_refusal_motive')}</Link>,
        },
        {
          key: 'admin/vintages',
          label: <Link to="admin/vintages">{i18n('menu.admin_vintages')}</Link>,
          permissions: permissions.adminVintages,
        },
        {
          key: 'admin/training-wish/priorities',
          label: <Link to="admin/training-wish/priorities">{i18n('menu.admin.priorities')}</Link>,
        },
        {
          key: 'admin/training/projects',
          label: <Link to="admin/training/projects">{i18n('menu.admin_training_projects')}</Link>,
        },
        {
          key: 'admin/training-wish/delete-reasons',
          label: <Link to="admin/training-wish/delete-reasons">{i18n('menu.admin_delete_reasons')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_session',
      label: i18n('menu.admin_session'),
      permissions: permissions.adminSessionManagement,
      children: [
        {
          key: 'admin/session/training-rooms',
          label: <Link to="admin/session/training-rooms">{i18n('training_rooms')}</Link>,
          permissions: permissions.adminSessionTrainingRooms,
        },
        {
          key: 'admin/session/cancellation-reasons',
          label: <Link to="admin/session/cancellation-reasons">{i18n('cancellation_reasons')}</Link>,
          permissions: permissions.adminSessionCancelationReasons,
        },
        {
          key: 'admin/session/absence-reasons',
          label: <Link to="admin/session/absence-reasons">{i18n('absence_reasons')}</Link>,
          permissions: permissions.adminSessionAbsenceReasons,
        },
        {
          key: 'admin/session/participant-information',
          label: <Link to="admin/session/participant-information">{i18n('participant_information')}</Link>,
          permissions: permissions.adminSessionParticipantInformation,
        },
        {
          key: 'admin/session/costs',
          label: <Link to="admin/session/costs">{i18n('admin_costs')}</Link>,
          permissions: permissions.adminSessionCosts,
        },
        {
          key: 'admin/animation-locales',
          label: <Link to="admin/animation-locales">{i18n('menu.admin_animation_locales')}</Link>,
          permissions: permissions.adminAnimationLocales,
        },
        {
          key: 'admin/session/types',
          label: <Link to="admin/session/types">{i18n('menu.admin_session_types')}</Link>,
          permissions: permissions.adminSessionTypes,
        },
        {
          key: 'admin/session/locations',
          label: <Link to="admin/session/locations">{i18n('menu.admin_session_locations')}</Link>,
          permissions: permissions.adminSessionLocations,
        },
        {
          key: 'admin/session/tasks',
          label: <Link to="admin/session/tasks">{i18n('menu.admin_session_tasks')}</Link>,
          permissions: permissions.adminSessionTasks,
        },
        {
          key: 'admin/session/task-lists',
          label: <Link to="admin/session/task-lists">{i18n('menu.admin_session_task_lists')}</Link>,
          permissions: permissions.adminSessionTasks,
        },
        {
          key: 'admin/session/custom-fields',
          label: <Link to="admin/session/custom-fields">{i18n('session.custom-fields.title')}</Link>,
          permissions: permissions.adminCustomFields,
        },
        {
          key: 'admin/session/room-layouts',
          label: <Link to="admin/session/room-layouts">{i18n('session.room-layouts.title')}</Link>,
        },
        {
          key: 'admin/session/teaching-materials',
          label: <Link to="admin/session/teaching-materials">{i18n('session.teaching-materials.title')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_plan',
      label: i18n('menu.admin_plan'),
      permissions: permissions.adminTrainingPlan,
      children: [
        {
          key: 'admin/training-wish/cancellation-reasons',
          label: <Link to="admin/training-wish/cancellation-reasons">{i18n('cancellation_reason')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_users_organizations',
      label: i18n('menu.admin_users_organizations'),
      permissions: permissions.adminUsersOrganizations,
      children: [
        {
          key: 'admin/session/organizations',
          label: <Link to="admin/session/organizations">{i18n('admin_organization')}</Link>,
        },
        {
          key: 'admin/session/organization-types',
          label: <Link to="admin/session/organization-types">{i18n('admin_organization_type')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_matrix',
      label: i18n('menu.admin_matrix'),
      permissions: permissions.adminMatrix,
      children: [
        {
          key: 'admin/matrix/level-templates',
          label: <Link to="admin/matrix/level-templates">{i18n('admin_matrix_level_templates')}</Link>,
        },
        {
          key: 'admin/matrix/skill-types',
          label: <Link to="admin/matrix/skill-types">{i18n('admin_matrix_skill_types')}</Link>,
        },
        {
          key: 'admin/matrix/skills',
          label: <Link to="admin/matrix/skills">{i18n('admin_matrix_skills')}</Link>,
        },
        {
          key: 'admin/matrix/job-families',
          label: <Link to="admin/matrix/job-families">{i18n('admin_matrix_job_family')}</Link>,
        },
        {
          key: 'admin/matrix/jobs',
          label: <Link to="admin/matrix/jobs">{i18n('admin_matrix_jobs')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_survey',
      label: i18n('menu.admin_survey'),
      permissions: permissions.adminSurveys,
      children: [
        {
          key: 'admin/surveys',
          label: <Link to="admin/surveys">{i18n('label_surveys')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_email',
      label: i18n('menu.admin_email'),
      permissions: permissions.adminEmails,
      children: [
        {
          key: 'admin/email-templates',
          label: <Link to="admin/email-templates">{i18n('admin_email_templates')}</Link>,
          permissions: permissions.adminEmailTemplates,
        },
        {
          key: 'admin/email-scheduled',
          label: <Link to="admin/email-scheduled">{i18n('menu.admin_email_scheduled')}</Link>,
          permissions: permissions.adminEmailScheduled,
        },
        {
          key: 'admin/email-sent',
          label: <Link to="admin/email-sent">{i18n('menu.admin_email_sent')}</Link>,
          permissions: permissions.adminEmailSent,
        },
      ],
    },
    {
      key: 'menu.admin_import',
      label: i18n('menu.admin_import'),
      permissions: permissions.adminImports,
      children: [
        {
          key: 'admin/import',
          label: <Link to="admin/import">{i18n('label.import')}</Link>,
        },
        {
          key: 'admin/configurable-exports',
          label: <Link to="admin/configurable-exports">{i18n('label.configurable_exports')}</Link>,
          permissions: permissions.configurableExports,
        },
      ],
    },
    {
      key: 'menu.admin_automations',
      label: i18n('menu.admin_automations'),
      permissions: permissions.adminAutomations,
      children: [
        {
          key: 'admin/automations',
          label: <Link to="admin/automations">{i18n('label.automations')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_accreditation_management',
      label: i18n('menu.admin_accreditation_management'),
      permissions: permissions.adminAccreditationManagement,
      children: [
        {
          key: 'admin/accreditation/domains',
          label: <Link to="admin/accreditation/domains">{i18n('menu.admin_accreditation_domains')}</Link>,
        },
        {
          key: 'admin/accreditation/accreditations',
          label: <Link to="admin/accreditation/accreditations">{i18n('menu.admin_accreditations')}</Link>,
        },
        {
          key: 'admin/accreditation/prerequisites',
          label: <Link to="admin/accreditation/prerequisites">{i18n('menu.admin_accreditation_prerequisites')}</Link>,
        },
        {
          key: 'admin/accreditation/signings',
          label: <Link to="admin/accreditation/signings">{i18n('menu.admin_accreditation_signings')}</Link>,
        },
        {
          key: 'admin/accreditation/application-fields',
          label: (
            <Link to="admin/accreditation/application-fields">
              {i18n('menu.admin_accreditation_application_fields')}
            </Link>
          ),
        },
        {
          key: 'admin/accreditation/trigger-mails',
          label: (
            <Link to="admin/accreditation/trigger-mails">{i18n('menu.admin_accreditation_mail_configuration')}</Link>
          ),
        },
      ],
    },
  ]

  return { coreItems, adminItems }
}
